export type ContainerScrollPosition = {
  scrollTop: number;
  pageCount: number;
};

export type ScrollLocation = {
  key?: string;
  pathname: string;
  search: string;
  state?: unknown;
};

type ScrollEntry = {
  y: number;
  containers: Record<string, ContainerScrollPosition>;
};

type ScrollStore = {
  byKey: Record<string, ScrollEntry>;
  byPath: Record<string, ScrollEntry>;
};

const STORAGE_KEY = 'varroc-scroll-restoration-v1';
const RESTORE_STATE_FLAG = '__restoreScroll';
const MAX_ENTRIES = 50;
const PERSIST_DELAY_MS = 300;

let store: ScrollStore | null = null;
let persistTimeoutId: ReturnType<typeof setTimeout> | null = null;
let isPersistDisabled = false;

const isBrowser = (): boolean => typeof window !== 'undefined';

const createEmptyStore = (): ScrollStore => ({ byKey: {}, byPath: {} });

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  !!value && typeof value === 'object' && !Array.isArray(value)
);

const readStore = (): ScrollStore => {
  if (store) {
    return store;
  }

  store = createEmptyStore();

  if (!isBrowser()) {
    return store;
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (isPlainObject(parsed)) {
      store = {
        byKey: isPlainObject(parsed.byKey) ? parsed.byKey as Record<string, ScrollEntry> : {},
        byPath: isPlainObject(parsed.byPath) ? parsed.byPath as Record<string, ScrollEntry> : {},
      };
    }
  } catch (error) {
    store = createEmptyStore();
  }

  return store;
};

const persist = (): void => {
  if (!isBrowser() || !store || isPersistDisabled) {
    return;
  }

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (error) {
    isPersistDisabled = true;
  }
};

const schedulePersist = (): void => {
  if (!isBrowser() || persistTimeoutId) {
    return;
  }

  persistTimeoutId = setTimeout(() => {
    persistTimeoutId = null;
    persist();
  }, PERSIST_DELAY_MS);
};

const flushStore = (): void => {
  if (persistTimeoutId) {
    clearTimeout(persistTimeoutId);
    persistTimeoutId = null;
  }
  persist();
};

const trimEntries = (entries: Record<string, ScrollEntry>): void => {
  const ids = Object.keys(entries);
  if (ids.length <= MAX_ENTRIES) {
    return;
  }

  ids.slice(0, ids.length - MAX_ENTRIES).forEach((id) => {
    delete entries[id];
  });
};

const getPathId = (location: ScrollLocation): string => `${location.pathname}${location.search}`;

const getKeyId = (location: ScrollLocation): string | null => (
  location.key ? `${location.key}|${getPathId(location)}` : null
);

const readEntry = (location: ScrollLocation): ScrollEntry | undefined => {
  const data = readStore();
  const keyId = getKeyId(location);
  const fromKey = keyId ? data.byKey[keyId] : undefined;
  return fromKey || data.byPath[getPathId(location)];
};

const writeEntry = (location: ScrollLocation, entry: ScrollEntry): void => {
  const data = readStore();
  const keyId = getKeyId(location);
  const pathId = getPathId(location);

  if (keyId) {
    delete data.byKey[keyId];
    data.byKey[keyId] = entry;
    trimEntries(data.byKey);
  }

  delete data.byPath[pathId];
  data.byPath[pathId] = entry;
  trimEntries(data.byPath);

  schedulePersist();
};

const updateEntry = (
  location: ScrollLocation,
  update: (entry: ScrollEntry) => ScrollEntry,
): void => {
  const current = readEntry(location);
  writeEntry(location, update({
    y: current?.y || 0,
    containers: { ...(current?.containers || {}) },
  }));
};

const toSafeNumber = (value: number, min: number): number => {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.max(min, Math.round(value));
};

export const saveWindowScrollPosition = (location: ScrollLocation, y: number): void => {
  updateEntry(location, (entry) => ({ ...entry, y: toSafeNumber(y, 0) }));
};

export const saveContainerScrollPosition = (
  location: ScrollLocation,
  containerKey: string,
  position: ContainerScrollPosition,
): void => {
  updateEntry(location, (entry) => ({
    ...entry,
    containers: {
      ...entry.containers,
      [containerKey]: {
        scrollTop: toSafeNumber(position.scrollTop, 0),
        pageCount: toSafeNumber(position.pageCount, 1),
      },
    },
  }));
};

const hasRestoreFlag = (state: unknown): boolean => (
  isPlainObject(state) && state[RESTORE_STATE_FLAG] === true
);

export const shouldRestoreScrollPosition = (navigationType: string, state?: unknown): boolean => (
  navigationType === 'POP' || hasRestoreFlag(state)
);

const readRestorableEntry = (
  location: ScrollLocation,
  navigationType: string,
): ScrollEntry | undefined => {
  if (!shouldRestoreScrollPosition(navigationType, location.state)) {
    return undefined;
  }
  return readEntry(location);
};

export const getRestorableWindowPosition = (
  location: ScrollLocation,
  navigationType: string,
): number | undefined => {
  const entry = readRestorableEntry(location, navigationType);
  return entry && entry.y > 0 ? entry.y : undefined;
};

export const getRestorableContainerPosition = (
  location: ScrollLocation,
  navigationType: string,
  containerKey: string,
): ContainerScrollPosition | undefined => {
  const entry = readRestorableEntry(location, navigationType);
  const position = entry?.containers?.[containerKey];

  if (!position || (position.scrollTop <= 0 && position.pageCount <= 1)) {
    return undefined;
  }

  return position;
};

export const withScrollRestoreState = (state?: unknown): unknown => {
  if (state === null || state === undefined) {
    return { [RESTORE_STATE_FLAG]: true };
  }

  if (!isPlainObject(state)) {
    return state;
  }

  return { ...state, [RESTORE_STATE_FLAG]: true };
};

if (isBrowser()) {
  window.addEventListener('pagehide', flushStore);
}
